import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment-timezone';
import {
  browserHealthEntry,
  browserHealthLevel,
  browserMonitoringStatusLabel,
  browserMonitoringHealthLevel,
  browserMonitoringSideDetail,
  formatPendingAgeSeconds,
} from '../utils/monitoringPresentation';
import { apiClient } from '../services/api';
import { splitDiagnosticsLogBundle } from '../utils/diagnosticsLogBundle';
import { useAuth } from '../contexts/AuthContext';

import { diagnosticsHealthColor } from '../utils/diagnosticsHealth';

const DiagnosticMiniCard: React.FC<{ title: string; status: string; detail?: string; level?: string }> = ({
  title, status, detail, level,
}) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Typography variant="caption" color="text.secondary">{title}</Typography>
      <Typography variant="subtitle2" component="div">{status}</Typography>
      {detail && <Typography variant="caption" display="block">{detail}</Typography>}
      {level && (
        <Chip
          size="small"
          color={diagnosticsHealthColor(level)}
          label={level}
          sx={{ mt: 0.5 }}
          aria-label={`${title} ${status} (${level})`}
        />
      )}
    </CardContent>
  </Card>
);

const logPreSx = {
  m: 0,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: 12,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
} as const;

const LogPane: React.FC<{ title: string; caption: string; text: string; testId: string; emptyLabel: string }> = ({
  title, caption, text, testId, emptyLabel,
}) => (
  <Box>
    <Typography variant="subtitle2">{title}</Typography>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
      {caption}
    </Typography>
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        maxHeight: 360,
        overflow: 'auto',
        bgcolor: 'grey.900',
        color: 'grey.100',
      }}
      data-testid={testId}
    >
      <Typography component="pre" sx={logPreSx}>
        {text.trim() ? text : emptyLabel}
      </Typography>
    </Paper>
  </Box>
);

const DeviceDiagnosticsPage: React.FC<{ embedded?: boolean; overviewOnly?: boolean }> = ({ embedded = false, overviewOnly = false }) => {
  const { deviceId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const permissions = user?.permissions || [];
  const canReadDiagnostics = permissions.includes('monitoring.diagnostics.read')
    || user?.role === 'super_user'
    || user?.role === 'administrator';
  const [refreshInterval, setRefreshInterval] = useState<number>(30000);
  const [pendingLogSnapshotId, setPendingLogSnapshotId] = useState<string | null>(null);
  const [selectedLogSnapshotId, setSelectedLogSnapshotId] = useState<string | null>(null);
  const [logPullError, setLogPullError] = useState<string | null>(null);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['deviceDiagnostics', deviceId],
    queryFn: () => apiClient.monitoring.getDeviceDiagnostics(deviceId),
    enabled: canReadDiagnostics && !!deviceId,
    refetchInterval: refreshInterval > 0 ? refreshInterval : false,
  });

  const { data: agentLogsData, refetch: refetchAgentLogs } = useQuery({
    queryKey: ['deviceAgentLogs', deviceId],
    queryFn: () => apiClient.monitoring.listDeviceAgentLogs(deviceId, { limit: 10 }),
    enabled: canReadDiagnostics && !!deviceId && !overviewOnly,
    refetchInterval: pendingLogSnapshotId ? 5000 : false,
  });

  const { data: selectedLogData, isFetching: selectedLogFetching } = useQuery({
    queryKey: ['deviceAgentLogSnapshot', deviceId, selectedLogSnapshotId],
    queryFn: () => apiClient.monitoring.getDeviceAgentLogSnapshot(deviceId, selectedLogSnapshotId!),
    enabled: canReadDiagnostics && !!deviceId && !!selectedLogSnapshotId,
  });

  const runDiagnostics = useMutation({
    mutationFn: () => apiClient.monitoring.runDeviceDiagnostics(deviceId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['deviceDiagnostics', deviceId] }),
  });

  const runActivitySelfTest = useMutation({
    mutationFn: () => apiClient.monitoring.runActivityPipelineSelfTest(deviceId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['deviceDiagnostics', deviceId] }),
  });

  const verifyPilot = useMutation({
    mutationFn: (testType: string) => apiClient.monitoring.verifyPilotTest(deviceId, { test_type: testType }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['deviceDiagnostics', deviceId] }),
  });

  const pullAgentLogs = useMutation({
    mutationFn: () => apiClient.monitoring.pullDeviceAgentLogs(deviceId),
    onSuccess: (res) => {
      setLogPullError(null);
      const snap = res?.data?.data;
      const id = snap?.id ? String(snap.id) : null;
      if (id) {
        setPendingLogSnapshotId(id);
        setSelectedLogSnapshotId(id);
      }
      void refetchAgentLogs();
    },
    onError: (err: any) => {
      setLogPullError(err?.response?.data?.message || err?.message || 'Failed to queue agent log pull.');
    },
  });

  const agentLogSnapshots = agentLogsData?.data?.data || [];
  const selectedLogSnapshot = selectedLogData?.data?.data;
  const selectedLogBundle = splitDiagnosticsLogBundle(String(selectedLogSnapshot?.content_text || ''));
  const agentLogText = selectedLogSnapshot
    ? String(
      selectedLogSnapshot.agent_log_text
      ?? (selectedLogBundle.bundled ? selectedLogBundle.agent : selectedLogSnapshot.content_text || ''),
    )
    : '';
  const serviceLogText = selectedLogSnapshot
    ? String(selectedLogSnapshot.service_log_text ?? selectedLogBundle.service ?? '')
    : '';

  useEffect(() => {
    if (!pendingLogSnapshotId || !agentLogSnapshots.length) return;
    const match = agentLogSnapshots.find((s: any) => s.id === pendingLogSnapshotId);
    if (match && (match.status === 'uploaded' || match.status === 'failed')) {
      setPendingLogSnapshotId(null);
      setSelectedLogSnapshotId(pendingLogSnapshotId);
    }
  }, [agentLogSnapshots, pendingLogSnapshotId]);

  const diagnostics = data?.data?.data;
  const issues = diagnostics?.issues || [];
  const readiness = diagnostics?.readiness;

  const headerHealth = useMemo(() => {
    const level = diagnostics?.device?.health_level || 'unknown';
    return level.toUpperCase();
  }, [diagnostics]);

  const copyDeviceId = async () => {
    if (!diagnostics?.device?.id) return;
    try {
      await navigator.clipboard.writeText(diagnostics.device.id);
    } catch {
      // ignore
    }
  };

  if (!canReadDiagnostics) {
    return (
      <Box sx={{ mt: embedded ? 0 : 4 }}>
        <Alert severity="warning">Device diagnostics require the monitoring.diagnostics.read permission.</Alert>
      </Box>
    );
  }

  if (isLoading || !diagnostics) {
    return (
      <Box sx={{ mt: embedded ? 0 : 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const content = (
    <>
      {!embedded && (
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/workforce-monitoring/computers')} sx={{ mb: 2 }}>
          Back to Computers
        </Button>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h5">{diagnostics.device.name}</Typography>
            <Typography variant="body2">{diagnostics.employee?.name || 'No employee assigned'}</Typography>
            <Typography variant="body2" color="text.secondary">
              {diagnostics.assignment?.current_campaign || diagnostics.schedule?.campaign?.name || 'No campaign'}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
              <Typography variant="caption" color="text.secondary">
                Device ID: {diagnostics.device.id}
              </Typography>
              <Button size="small" onClick={copyDeviceId}>Copy</Button>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Chip color={diagnostics.device.online ? 'success' : 'default'} label={diagnostics.device.online ? 'ONLINE' : 'OFFLINE'} />
            <Typography variant="body2" sx={{ mt: 1 }}>Agent Health: {headerHealth}</Typography>
            <Typography variant="caption" color="text.secondary">
              Last heartbeat: {diagnostics.device.last_heartbeat_label || 'Unknown'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<PlayArrowIcon />}
                onClick={() => runDiagnostics.mutate()}
                disabled={runDiagnostics.isPending}
              >
                Run Diagnostics
              </Button>
              <Button
                variant="outlined"
                onClick={() => runActivitySelfTest.mutate()}
                disabled={runActivitySelfTest.isPending}
              >
                Activity Self-Test
              </Button>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => refetch()} disabled={isFetching}>
                Refresh
              </Button>
            </Stack>
            <FormControl size="small" sx={{ mt: 1, minWidth: 140 }}>
              <InputLabel>Auto Refresh</InputLabel>
              <Select
                label="Auto Refresh"
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(Number(e.target.value))}
              >
                <MenuItem value={0}>Off</MenuItem>
                <MenuItem value={10000}>10 sec</MenuItem>
                <MenuItem value={30000}>30 sec</MenuItem>
                <MenuItem value={60000}>60 sec</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {runActivitySelfTest.isSuccess && (
        <Alert severity={runActivitySelfTest.data?.data?.data?.passed ? 'success' : 'warning'} sx={{ mb: 2 }}>
          Activity pipeline self-test {runActivitySelfTest.data?.data?.data?.passed ? 'passed' : 'failed'}
          {runActivitySelfTest.data?.data?.data?.event_id
            ? ` (event ${runActivitySelfTest.data.data.data.event_id})`
            : ''}
          . Diagnostic events are not paid work.
        </Alert>
      )}
      {runActivitySelfTest.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>Activity pipeline self-test failed to run.</Alert>
      )}

      <Card sx={{ mb: 2, bgcolor: readiness?.ready_for_pilot ? 'success.50' : 'warning.50' }}>
        <CardContent>
          <Typography variant="h6">
            {readiness?.ready_for_pilot ? '✓ READY FOR PILOT' : '⚠ NOT READY FOR PILOT'}
          </Typography>
          <Typography variant="body2">
            {readiness?.passed_required} of {readiness?.total_required} required checks passed
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Last verified: {moment(readiness?.last_verified_at).format('h:mm:ss A')}
          </Typography>
          {!readiness?.ready_for_pilot && readiness?.problems?.length > 0 && (
            <Box mt={1}>
              {readiness.problems.map((problem: string) => (
                <Typography key={problem} variant="body2">• {problem}</Typography>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}><DiagnosticMiniCard title="Agent" status={diagnostics.agent.installed_version || 'Unknown'} detail={`Expected ${diagnostics.agent.expected_version}`} level={diagnostics.agent.version_status === 'outdated' ? 'warning' : 'healthy'} /></Grid>
        <Grid item xs={6} md={3}><DiagnosticMiniCard title="Service" status={diagnostics.service.status} detail={diagnostics.service.last_seen_label || ''} level={diagnostics.service.health_level} /></Grid>
        <Grid item xs={6} md={3}><DiagnosticMiniCard title="Companion" status={diagnostics.companion.status} detail={diagnostics.companion.last_seen_label || ''} level={diagnostics.companion.health_level} /></Grid>
        <Grid item xs={6} md={3}><DiagnosticMiniCard title="Authentication" status={diagnostics.authentication.secure_device_token ? 'Secure Device Token' : diagnostics.authentication.authentication_method} level={diagnostics.authentication.secure_device_token ? 'healthy' : 'warning'} /></Grid>
        <Grid item xs={6} md={3}><DiagnosticMiniCard title="Schedule" status={diagnostics.schedule?.found ? 'Loaded' : 'Not found'} detail={diagnostics.assignment?.schedule_label || ''} level={diagnostics.schedule?.found ? 'healthy' : 'info'} /></Grid>
        <Grid item xs={6} md={3}><DiagnosticMiniCard title="Work State" status={diagnostics.work_state?.work_state || '—'} detail={diagnostics.work_state?.clock_status || ''} level="healthy" /></Grid>
        <Grid item xs={6} md={3}><DiagnosticMiniCard title="Activity" status={diagnostics.activity.receiving ? 'Receiving' : 'No recent activity'} detail={diagnostics.activity.last_application || ''} level={diagnostics.activity.receiving ? 'healthy' : 'warning'} /></Grid>
        {diagnostics.activity_monitoring && (
          <>
            <Grid item xs={6} md={3}><DiagnosticMiniCard title="Monitoring Engine" status={diagnostics.activity_monitoring.monitoring_engine || diagnostics.activity_monitoring.foreground_tracker} detail="Independent of TEKS sign-in / Clock In" level={String(diagnostics.activity_monitoring.monitoring_engine || '').startsWith('Running') || String(diagnostics.activity_monitoring.foreground_tracker).startsWith('Running') ? 'healthy' : 'warning'} /></Grid>
            <Grid item xs={6} md={3}><DiagnosticMiniCard title="Foreground Tracker" status={diagnostics.activity_monitoring.foreground_tracker} detail={diagnostics.activity_monitoring.employee_context} level={String(diagnostics.activity_monitoring.foreground_tracker).startsWith('Running') ? 'healthy' : 'warning'} /></Grid>
            <Grid item xs={6} md={3}><DiagnosticMiniCard title="Action Recorder" status={diagnostics.activity_monitoring.action_recorder} detail={diagnostics.activity_monitoring.work_context} level={String(diagnostics.activity_monitoring.action_recorder || '').includes('Policy') ? 'info' : 'healthy'} /></Grid>
            <Grid item xs={6} md={3}><DiagnosticMiniCard title="Browser Listener" status={diagnostics.activity_monitoring.browser_listener || diagnostics.activity_monitoring.browser_extension} level="info" /></Grid>
            {diagnostics.browser_monitoring ? (
              <>
                {(['chrome', 'edge'] as const).map((key) => {
                  const side = diagnostics.browser_monitoring[key];
                  const name = key === 'chrome' ? 'Chrome' : 'Edge';
                  return (
                    <Grid item xs={6} md={3} key={key}>
                      <DiagnosticMiniCard
                        title={`${name} Monitoring`}
                        status={browserMonitoringStatusLabel(side?.status)}
                        detail={browserMonitoringSideDetail(side)}
                        level={browserMonitoringHealthLevel(side?.status)}
                      />
                    </Grid>
                  );
                })}
                <Grid item xs={6} md={3}>
                  <DiagnosticMiniCard
                    title="Browser Correlation"
                    status={browserMonitoringStatusLabel(diagnostics.browser_monitoring.correlation?.status)}
                    detail={[
                      diagnostics.browser_monitoring.correlation?.initialized ? 'Initialized' : 'Not initialized',
                      diagnostics.browser_monitoring.correlation?.last_web_active_produced_label
                        || (diagnostics.browser_monitoring.correlation?.last_web_active_produced_at
                          ? `Last interval ${moment(diagnostics.browser_monitoring.correlation.last_web_active_produced_at).fromNow()}`
                          : 'No foreground interval yet'),
                      diagnostics.browser_monitoring.correlation?.last_close_reason
                        ? `Close: ${diagnostics.browser_monitoring.correlation.last_close_reason}`
                        : null,
                    ].filter(Boolean).join(' · ')}
                    level={browserMonitoringHealthLevel(diagnostics.browser_monitoring.correlation?.status)}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <DiagnosticMiniCard
                    title="Delivery Queue"
                    status={browserMonitoringStatusLabel(diagnostics.browser_monitoring.delivery_queue?.status)}
                    detail={[
                      diagnostics.browser_monitoring.delivery_queue?.pending_count != null
                        ? `${diagnostics.browser_monitoring.delivery_queue.pending_count} pending`
                        : null,
                      diagnostics.browser_monitoring.delivery_queue?.oldest_pending_age_seconds != null
                        ? `Oldest ${formatPendingAgeSeconds(diagnostics.browser_monitoring.delivery_queue.oldest_pending_age_seconds)}`
                        : null,
                      diagnostics.browser_monitoring.delivery_queue?.last_upload_success_at
                        ? `Last OK ${moment(diagnostics.browser_monitoring.delivery_queue.last_upload_success_at).fromNow()}`
                        : null,
                      diagnostics.browser_monitoring.delivery_queue?.upload_status === 'DEGRADED'
                        ? (diagnostics.browser_monitoring.delivery_queue.last_error_category
                          ? `Upload: ${diagnostics.browser_monitoring.delivery_queue.last_error_category}`
                          : 'Upload degraded')
                        : null,
                    ].filter(Boolean).join(' · ') || undefined}
                    level={browserMonitoringHealthLevel(diagnostics.browser_monitoring.delivery_queue?.status)}
                  />
                </Grid>
              </>
            ) : (
              (['Chrome', 'Edge'] as const).map((browser) => {
                const entry = browserHealthEntry(diagnostics.browser_extension, browser);
                return (
                  <Grid item xs={6} md={3} key={browser}>
                    <DiagnosticMiniCard
                      title={`${browser} Extension`}
                      status={diagnostics.browser_extension?.enabled === false ? 'Disabled' : entry.status}
                      detail={entry.detail}
                      level={diagnostics.browser_extension?.enabled === false ? 'info' : browserHealthLevel(entry.status)}
                    />
                  </Grid>
                );
              })
            )}
            <Grid item xs={6} md={3}><DiagnosticMiniCard title="Activity Queue" status={diagnostics.activity_monitoring.activity_queue || diagnostics.activity_monitoring.queue} detail={diagnostics.activity_monitoring.pending_events != null ? `${diagnostics.activity_monitoring.pending_events} pending` : undefined} level={(diagnostics.activity_monitoring.activity_queue || diagnostics.activity_monitoring.queue) === 'Healthy' ? 'healthy' : 'warning'} /></Grid>
            <Grid item xs={6} md={3}><DiagnosticMiniCard title="Uploader" status={diagnostics.activity_monitoring.uploader || '—'} level={diagnostics.activity_monitoring.uploader === 'Healthy' ? 'healthy' : 'warning'} /></Grid>
          </>
        )}
        {diagnostics.teks_context && (
          <>
            <Grid item xs={6} md={3}><DiagnosticMiniCard title="TEKS Employee" status={diagnostics.teks_context.employee} detail={diagnostics.teks_context.employee_detail} level="info" /></Grid>
            <Grid item xs={6} md={3}><DiagnosticMiniCard title="TEKS Authentication" status={diagnostics.teks_context.authentication} level="info" /></Grid>
            <Grid item xs={6} md={3}><DiagnosticMiniCard title="Attendance State" status={diagnostics.teks_context.attendance_state} level="info" /></Grid>
            <Grid item xs={6} md={3}><DiagnosticMiniCard title="Work Session" status={diagnostics.teks_context.work_session} detail={diagnostics.teks_context.work_state || undefined} level="info" /></Grid>
          </>
        )}
        {diagnostics.web_activity?.release_enabled && diagnostics.web_activity?.status !== 'DEFERRED' && (
          <Grid item xs={6} md={3}><DiagnosticMiniCard title="Web Activity" status={diagnostics.web_activity?.status || (diagnostics.browser_extension.enabled ? 'UNKNOWN' : 'DISABLED')} detail={[
            diagnostics.web_activity?.last_web_active_produced_at
              ? `Last WEB_ACTIVE ${moment(diagnostics.web_activity.last_web_active_produced_at).fromNow()}`
              : diagnostics.web_activity?.last_event_label,
            diagnostics.browser_monitoring?.pipeline_label,
          ].filter(Boolean).join(' · ')} level={diagnostics.web_activity?.status === 'RECEIVING' ? 'healthy' : diagnostics.web_activity?.status === 'DISABLED' ? 'info' : 'warning'} /></Grid>
        )}
        <Grid item xs={6} md={3}><DiagnosticMiniCard title="Idle Engine" status={diagnostics.idle_engine.enabled ? 'Enabled' : 'Disabled'} detail={`Threshold ${Math.round((diagnostics.idle_engine.threshold_seconds || 0) / 60)} min`} level="healthy" /></Grid>
        <Grid item xs={6} md={3}><DiagnosticMiniCard title="API" status={diagnostics.connectivity.api_status} detail={diagnostics.connectivity.last_successful_sync_label || ''} level="healthy" /></Grid>
        <Grid item xs={6} md={3}><DiagnosticMiniCard title="Queue" status={diagnostics.connectivity.pending_queue_reported ? String(diagnostics.connectivity.pending_local_queue_count) : 'Not reported'} detail={diagnostics.connectivity.oldest_queued_event_at ? `Oldest ${moment(diagnostics.connectivity.oldest_queued_event_at).fromNow()}` : 'Local queue count requires agent telemetry'} level="info" /></Grid>
        <Grid item xs={6} md={3}><DiagnosticMiniCard title="Live Idle" status={diagnostics.idle_engine.current_idle_seconds != null ? `${diagnostics.idle_engine.current_idle_seconds}s` : 'Not reported'} detail={`Threshold ${diagnostics.idle_engine.threshold_seconds}s`} level="info" /></Grid>
        <Grid item xs={6} md={3}><DiagnosticMiniCard title="Employee Token" status={diagnostics.employee?.token_status || '—'} detail={diagnostics.employee?.token_expiration ? moment(diagnostics.employee.token_expiration).format('MMM D h:mm A') : 'Expiration requires agent telemetry'} level={diagnostics.employee?.token_status === 'valid' ? 'healthy' : 'warning'} /></Grid>
        <Grid item xs={6} md={3}><DiagnosticMiniCard title="Campaign" status={diagnostics.assignment.campaign_mismatch ? 'MISMATCH' : 'Matched'} detail={diagnostics.assignment.current_campaign || ''} level={diagnostics.assignment.campaign_mismatch ? 'critical' : 'healthy'} /></Grid>
        {diagnostics.action_recording && (
          <Grid item xs={6} md={3}>
            <DiagnosticMiniCard
              title="Action Recorder"
              status={diagnostics.action_recording.enabled ? 'Enabled' : 'Disabled'}
              detail={[
                diagnostics.action_recording.evidence_snapshots ? 'Snapshots on' : 'Snapshots off',
                diagnostics.action_recording.last_event_at
                  ? `Last event ${moment(diagnostics.action_recording.last_event_at).fromNow()}`
                  : null,
              ].filter(Boolean).join(' · ') || undefined}
              level={diagnostics.action_recording.enabled ? 'healthy' : 'info'}
            />
          </Grid>
        )}
        {diagnostics.call_monitoring && (
          <>
            <Grid item xs={6} md={3}>
              <DiagnosticMiniCard
                title="Call Monitoring"
                status={diagnostics.call_monitoring.effective ? 'Effective ON' : 'Off'}
                detail={`Global ${diagnostics.call_monitoring.global_enabled ? 'ON' : 'OFF'} · Device ${diagnostics.call_monitoring.device_enabled ? 'ON' : 'OFF'}`}
                level={diagnostics.call_monitoring.effective ? 'healthy' : 'info'}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <DiagnosticMiniCard
                title="Call Detection"
                status={diagnostics.call_monitoring.detection_engine || diagnostics.call_monitoring.current_state || '—'}
                detail={diagnostics.call_monitoring.audio_capture || diagnostics.call_monitoring.capture_mode || undefined}
                level={diagnostics.call_monitoring.effective ? 'healthy' : 'info'}
              />
            </Grid>
          </>
        )}
        {diagnostics.enforcement?.policy && (
          <>
            <Grid item xs={6} md={3}>
              <DiagnosticMiniCard
                title="Enforcement Policy"
                status={diagnostics.enforcement.policy.policy_name || '—'}
                detail={diagnostics.enforcement.policy.policy_version != null ? `v${diagnostics.enforcement.policy.policy_version}` : undefined}
                level="info"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <DiagnosticMiniCard
                title="Clock In Level"
                status={diagnostics.enforcement.policy.clock_in_enforcement || '—'}
                level="info"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <DiagnosticMiniCard
                title="Lunch Return"
                status={diagnostics.enforcement.policy.lunch_return_enforcement || '—'}
                level="info"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <DiagnosticMiniCard
                title="Bathroom Return"
                status={diagnostics.enforcement.policy.bathroom_return_enforcement || '—'}
                level="info"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <DiagnosticMiniCard
                title="Idle Lock"
                status={diagnostics.enforcement.policy.idle_lock_enabled ? 'Enabled' : 'Disabled'}
                detail={
                  diagnostics.enforcement.policy.idle_threshold_minutes != null
                    ? `Threshold ${diagnostics.enforcement.policy.idle_threshold_minutes} min`
                    : undefined
                }
                level="info"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <DiagnosticMiniCard
                title="Last Policy Sync"
                status={
                  diagnostics.enforcement.policy.last_policy_sync
                    ? moment(diagnostics.enforcement.policy.last_policy_sync).format('MMM D h:mm A')
                    : '—'
                }
                detail={diagnostics.enforcement.policy.source || undefined}
                level="info"
              />
            </Grid>
          </>
        )}
      </Grid>

      {!overviewOnly && (
        <>
        <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Current Assignment</Typography>
          <Typography variant="body2">Employee: {diagnostics.assignment.employee_name || '—'}</Typography>
          <Typography variant="body2">Schedule: {diagnostics.assignment.schedule_label || '—'}</Typography>
          <Typography variant="body2">Primary Campaign: {diagnostics.assignment.primary_campaign || '—'}</Typography>
          <Typography variant="body2">Current Campaign: {diagnostics.assignment.current_campaign || '—'}</Typography>
          <Typography variant="body2">Assignment Type: {diagnostics.assignment.assignment_type}</Typography>
          <Typography variant="body2">Coverage: {diagnostics.assignment.coverage || 'None'}</Typography>
          {diagnostics.assignment.campaign_mismatch && (
            <Alert severity="error" sx={{ mt: 1 }}>CAMPAIGN MISMATCH — no valid coverage assignment detected.</Alert>
          )}
        </CardContent>
      </Card>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Issues Detected</Typography>
              {issues.length === 0 ? (
                <Typography variant="body2">No issues detected.</Typography>
              ) : issues.map((issue: any) => (
                <Box key={`${issue.problem}-${issue.severity}`} sx={{ mb: 1.5 }}>
                  <Chip size="small" color={diagnosticsHealthColor(issue.severity)} label={issue.severity.toUpperCase()} sx={{ mr: 1 }} />
                  <Typography variant="subtitle2" component="span">{issue.problem}</Typography>
                  <Typography variant="caption" display="block">Likely cause: {issue.likely_cause}</Typography>
                  <Typography variant="caption" display="block">Recommended: {issue.recommended_action}</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Recent Events</Typography>
              {(diagnostics.recent_events || []).map((event: any) => (
                <Typography key={`${event.type}-${event.timestamp}-${event.event_type}`} variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {moment(event.timestamp).format('HH:mm:ss')} {event.event_type}
                  {event.detail ? ` — ${event.detail}` : ''}
                </Typography>
              ))}
              {diagnostics.employee?.id && (
                <Button sx={{ mt: 1 }} size="small" onClick={() => navigate(`/workforce-monitoring/activity?employee_id=${diagnostics.employee.id}`)}>
                  View Full Activity Timeline
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Pilot Validation</Typography>
          <Grid container spacing={1}>
            {(diagnostics.pilot_validation || []).map((test: any) => (
              <Grid item xs={12} md={6} key={test.test_type}>
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                  <Box>
                    <Typography variant="body2">
                      {test.status === 'auto_verified' || test.status === 'manually_verified' ? '✓' : '○'} {test.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {test.status === 'auto_verified' && test.verified_at
                        ? `Auto Verified ${moment(test.verified_at).format('h:mm A')}`
                        : test.status === 'manually_verified' && test.verified_at
                          ? `Manually Verified ${moment(test.verified_at).format('h:mm A')}`
                          : 'Pending'}
                    </Typography>
                  </Box>
                  {!test.auto && test.status !== 'manually_verified' && (
                    <Button size="small" onClick={() => verifyPilot.mutate(test.test_type)} disabled={verifyPilot.isPending}>
                      Mark Verified
                    </Button>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mt: 2 }} data-testid="agent-log-pull-card">
        <CardContent>
          <Typography variant="h6" gutterBottom>Device logs</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Pull redacted tails from this computer. <strong>Agent (companion)</strong> is{' '}
            <code>agent.log</code>
            {diagnostics?.device?.logs_path ? ` (${diagnostics.device.logs_path})` : ''}.{' '}
            <strong>Service</strong> is the Windows Application event log for source{' '}
            <code>TEKSTimeAgent</code>, plus <code>service.log</code> when that file exists.
            Device token only — sign-in and clock-in are not required. Caps: up to 128 KB / 400 lines per log.
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              size="small"
              onClick={() => pullAgentLogs.mutate()}
              disabled={pullAgentLogs.isPending || Boolean(pendingLogSnapshotId)}
              data-testid="pull-agent-logs-button"
            >
              {pullAgentLogs.isPending || pendingLogSnapshotId ? (
                <CircularProgress size={16} sx={{ mr: 1 }} />
              ) : null}
              {pendingLogSnapshotId ? 'Waiting for device…' : 'Pull logs'}
            </Button>
            <Button size="small" onClick={() => void refetchAgentLogs()}>
              Refresh list
            </Button>
          </Stack>
          {logPullError && (
            <Alert severity="error" sx={{ mt: 1.5 }}>{logPullError}</Alert>
          )}
          {pendingLogSnapshotId && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              Pull queued. The Companion and the Windows service upload on their next heartbeats
              (usually within a minute) when the computer is online.
            </Alert>
          )}
          {agentLogSnapshots.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Recent pulls</Typography>
              <Stack spacing={0.5}>
                {agentLogSnapshots.map((snap: any) => (
                  <Box
                    key={snap.id}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    flexWrap="wrap"
                  >
                    <Button
                      size="small"
                      variant={selectedLogSnapshotId === snap.id ? 'contained' : 'outlined'}
                      onClick={() => setSelectedLogSnapshotId(snap.id)}
                    >
                      {snap.status}
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      {snap.requested_at ? moment(snap.requested_at).format('MMM D, h:mm:ss A') : '—'}
                      {snap.line_count != null ? ` · ${snap.line_count} lines` : ''}
                      {snap.size_bytes != null ? ` · ${snap.size_bytes} B` : ''}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
          {selectedLogSnapshot?.status === 'failed' && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              Pull failed: {selectedLogSnapshot.error_message || 'unknown error'}
            </Alert>
          )}
          {selectedLogSnapshot?.status === 'uploaded' && (
            <Box sx={{ mt: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">Log viewer</Typography>
                {selectedLogFetching && <CircularProgress size={14} />}
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => {
                    void apiClient.monitoring.downloadDeviceAgentLogSnapshot(
                      deviceId,
                      selectedLogSnapshot.id,
                      `device-logs-${deviceId}-${selectedLogSnapshot.id}.txt`,
                    );
                  }}
                  data-testid="download-agent-log-button"
                >
                  Download
                </Button>
              </Stack>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={6}>
                  <LogPane
                    title="Agent (companion)"
                    caption="agent.log"
                    text={agentLogText}
                    testId="agent-log-viewer"
                    emptyLabel="(no agent log in this pull)"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <LogPane
                    title="Service"
                    caption="Application event log · source TEKSTimeAgent"
                    text={serviceLogText}
                    testId="service-log-viewer"
                    emptyLabel="(no service log in this pull)"
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>
        </>
      )}
    </>
  );

  if (embedded) {
    return <Box sx={{ mb: 2 }}>{content}</Box>;
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
      {content}
    </Container>
  );
};

export default DeviceDiagnosticsPage;
